import { memo, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Components
import AccessControl from 'components/common/AccessControl';
import CampaignEmailActivity from './CampaignEmailActivity';
import CampaignCountOfData from './CampaignCountOfData';
import CampaignDataChart from './CampaignDataChart';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useHasPermission from 'hooks/UseHasPermission';
// Import Services
import { CampaignHttpService } from 'services/http';
// Import Constants
import { l } from 'constants/common';
// Import Enums
import { PermissionEnum } from 'enums';

const CampaignReportVisualization = ({ campaignId, startDate, endDate, isLoadingSchedule, isActive }) => {
    const { t } = useTranslation();

    const { doPostRequest } = useRequest();
    const { hasPermission } = useHasPermission();

    const [isLoadingCampaignEmailActivity, setIsLoadingCampaignEmailActivity] = useState(true);
    const [campaignEmailActivityData, setCampaignEmailActivityData] = useState({});

    const [isLoadingCampaignBonusChartsData, setIsLoadingCampaignBonusChartsData] = useState(true);
    const [campaignBonusChartsData, setCampaignBonusChartsData] = useState<AnyObject>({});

    const [isLoadingCampaignEmailChartsData, setIsLoadingCampaignEmailChartsData] = useState(true);
    const [campaignEmailChartsData, setCampaignEmailChartsData] = useState<AnyObject>({});

    const [isRequestBodyChanged, setIsRequestBodyChanged] = useState(false);

    const { getCampaignEmailActivityRequest, getCampaignBonusChartsDataRequest, getCampaignEmailChartsDataRequest } =
        useCancelableRequests(
            {
                getCampaignEmailActivityRequest: CampaignHttpService.getCampaignEmailActivity,
                getCampaignBonusChartsDataRequest: CampaignHttpService.getCampaignBonusChartsData,
                getCampaignEmailChartsDataRequest: CampaignHttpService.getCampaignEmailChartsData,
            },
            {
                takeLatest: [
                    'getCampaignEmailActivityRequest',
                    'getCampaignBonusChartsDataRequest',
                    'getCampaignEmailChartsDataRequest',
                ],
            },
        );

    const getEmailActivity = useCallback(() => {
        setIsLoadingCampaignEmailActivity(true);
        doPostRequest(getCampaignEmailActivityRequest.request, {
            requestBody: { CampaignId: campaignId, StartDate: startDate, EndDate: endDate },
            successCallback: (data) => {
                setCampaignEmailActivityData(data);
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsLoadingCampaignEmailActivity(false);
            }
        });
    }, [campaignId, startDate, endDate, doPostRequest, getCampaignEmailActivityRequest]);

    const getBonusCharts = useCallback(() => {
        setIsLoadingCampaignBonusChartsData(true);
        doPostRequest(getCampaignBonusChartsDataRequest.request, {
            requestBody: { CampaignId: campaignId, StartDate: startDate, EndDate: endDate },
            successCallback: (data) => {
                setCampaignBonusChartsData(data);
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsLoadingCampaignBonusChartsData(false);
            }
        });
    }, [campaignId, startDate, endDate, doPostRequest, getCampaignBonusChartsDataRequest]);

    const getEmailCharts = useCallback(() => {
        setIsLoadingCampaignEmailChartsData(true);
        doPostRequest(getCampaignEmailChartsDataRequest.request, {
            requestBody: { CampaignId: campaignId, StartDate: startDate, EndDate: endDate },
            successCallback: (data) => {
                setCampaignEmailChartsData(data);
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsLoadingCampaignEmailChartsData(false);
            }
        });
    }, [campaignId, startDate, endDate, doPostRequest, getCampaignEmailChartsDataRequest]);

    useEffect(() => {
        if (!isLoadingSchedule && isActive && isRequestBodyChanged) {
            getEmailActivity();
            hasPermission(PermissionEnum.ViewAwards) && getBonusCharts();
            getEmailCharts();

            setIsRequestBodyChanged(false);
        }
    }, [
        campaignId,
        startDate,
        endDate,
        isLoadingSchedule,
        isRequestBodyChanged,
        isActive,
        getEmailActivity,
        hasPermission,
        getBonusCharts,
        getEmailCharts,
    ]);

    useEffect(() => {
        setIsRequestBodyChanged(true);
    }, [campaignId, startDate, endDate]);

    return (
        <div className="c-report-visual-wrapper">
            <div className="c-report-visual-wrapper-inner">
                <AccessControl permissions={PermissionEnum.ViewAwards}>
                    <CampaignCountOfData
                        isLoading={isLoadingCampaignBonusChartsData}
                        data={isNil(campaignBonusChartsData?.chartsCount) ? [] : campaignBonusChartsData?.chartsCount}
                        title={t(l.CountOfSentBonus)}
                    />
                    <CampaignDataChart
                        isLoading={isLoadingCampaignBonusChartsData}
                        data={isNil(campaignBonusChartsData?.pieChart) ? {} : campaignBonusChartsData?.pieChart}
                        title={t(l.CountOfSentBonus)}
                    />
                </AccessControl>
                <CampaignCountOfData
                    isLoading={isLoadingCampaignEmailChartsData}
                    data={isNil(campaignEmailChartsData?.chartsCount) ? [] : campaignEmailChartsData?.chartsCount}
                    title={t(l.CountOfSentMessage)}
                />
                <CampaignDataChart
                    isLoading={isLoadingCampaignEmailChartsData}
                    data={isNil(campaignEmailChartsData?.pieChart) ? {} : campaignEmailChartsData?.pieChart}
                    title={t(l.CountOfSentMessage)}
                />
                <CampaignEmailActivity isLoading={isLoadingCampaignEmailActivity} data={campaignEmailActivityData} />
            </div>
        </div>
    );
};

CampaignReportVisualization.propTypes = {
    campaignId: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    isLoadingSchedule: PropTypes.bool.isRequired,
    isActive: PropTypes.bool,
};

CampaignReportVisualization.defaultProps = {
    isActive: true,
};

export default memo(CampaignReportVisualization);
